import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Tag,
  Space,
  Card,
  Select,
  Row,
  Col,
  Checkbox,
  DatePicker,
  Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchUsers();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase()) ||
        user.id.toString().includes(searchText)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.is_active : !user.is_active
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => 
        roleFilter === 'admin' ? user.is_admin : !user.is_admin
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchText, statusFilter, roleFilter]);

  // Automatically remove items no longer visible from selection when the filter changes
  useEffect(() => {
    const filteredIds = (filteredUsers || []).map(u => u.id);
    setSelectedRowKeys(prev => {
      const validSelectedKeys = prev.filter(key => filteredIds.includes(key));
      // Only update if there are changes
      if (validSelectedKeys.length !== prev.length) {
        return validSelectedKeys;
      }
      return prev;
    });
  }, [filteredUsers]);

  const fetchUsers = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/users/?skip=${(page - 1) * pageSize}&limit=${pageSize}`);
      console.log('Users data:', response.data); // Debug log
      
      // Handle paginated response
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated response
        const usersWithDefaults = (response.data.data || []).map(user => ({
          ...user,
          char_limit: user.char_limit !== undefined ? user.char_limit : 10000,
          used_chars: user.used_chars !== undefined ? user.used_chars : 0,
          plan_expires_at: user.plan_expires_at || null,
        }));
        
        setUsers(usersWithDefaults);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.total
        }));
      } else {
        // Non-paginated response (fallback)
        const usersWithDefaults = (response.data || []).map(user => ({
          ...user,
          char_limit: user.char_limit !== undefined ? user.char_limit : 10000,
          used_chars: user.used_chars !== undefined ? user.used_chars : 0,
          plan_expires_at: user.plan_expires_at || null,
        }));
        
        setUsers(usersWithDefaults);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.length
        }));
      }
    } catch (error) {
      message.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      char_limit: 10000,
      plan_expires_at: null,
    });
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      char_limit: user.char_limit || 10000, // Default to 10000 if not set
      is_active: user.is_active,
      plan_expires_at: user.plan_expires_at ? dayjs(user.plan_expires_at) : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`/api/users/${userId}`);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error('Failed to delete user');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select users to delete');
      return;
    }

    // Filter out admin users (deleting admins is not allowed)
    const selectedUsers = (filteredUsers || []).filter(u => selectedRowKeys.includes(u.id));
    const nonAdminUsers = selectedUsers.filter(u => !u.is_admin);
    const adminUsers = selectedUsers.filter(u => u.is_admin);

    if (nonAdminUsers.length === 0) {
      message.warning('Cannot delete admin users. Please select non-admin users only.');
      return;
    }

    if (adminUsers.length > 0) {
      message.warning(`Skipping ${adminUsers.length} admin user(s). Only deleting ${nonAdminUsers.length} non-admin user(s).`);
    }

    try {
      // Delete each user individually (batch API may not be available)
      const deletePromises = nonAdminUsers.map(user => 
        axios.delete(`/api/users/${user.id}`)
      );
      
      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        message.success(`Successfully deleted ${successful} user(s)`);
        setSelectedRowKeys([]);
        fetchUsers(pagination.current, pagination.pageSize);
      }

      if (failed > 0) {
        message.warning(`${failed} user(s) failed to delete`);
      }
    } catch (error) {
      console.error('❌ handleBatchDelete error:', error);
      message.error('Failed to delete selected users');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (payload.plan_expires_at) {
        payload.plan_expires_at = payload.plan_expires_at.toISOString();
      }
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, payload);
        message.success('User updated successfully');
      } else {
        await axios.post('/api/users/', payload);
        message.success('User created successfully');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setRoleFilter('all');
  };

  const handleSelectAll = () => {
    // Select all items on the current page (after filtering)
    const currentPageIds = (filteredUsers || []).map(u => u.id);
    setSelectedRowKeys(currentPageIds);
  };

  const handleUnselectAll = () => {
    // Deselect all
    setSelectedRowKeys([]);
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      handleSelectAll();
    } else {
      handleUnselectAll();
    }
  };

  // Check if all items on the current page are selected
  const isAllSelected = () => {
    const currentPageIds = (filteredUsers || []).map(u => u.id);
    if (currentPageIds.length === 0) return false;
    return currentPageIds.every(id => selectedRowKeys.includes(id));
  };

  // Check if any item is selected (indeterminate state)
  const isIndeterminate = () => {
    const currentPageIds = (filteredUsers || []).map(u => u.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRowKeys.includes(id));
    return selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (isAdmin) => (
        <Tag color={isAdmin ? 'blue' : 'default'}>
          {isAdmin ? 'Admin' : 'User'}
        </Tag>
      ),
    },
    {
      title: 'Char Limit',
      dataIndex: 'char_limit',
      key: 'char_limit',
      className: 'char-limit-cell',
      render: (limit) => {
        // Handle null, undefined, or very large values as unlimited
        const charLimit = Number(limit);
        const isUnlimited = charLimit === null || charLimit === undefined || charLimit >= 999999999;
        const isZero = charLimit === 0;
        
        console.log('Char limit debug:', {
          original: limit,
          type: typeof limit,
          processed: charLimit,
          isUnlimited,
          isZero,
          isNull: limit === null,
          isUndefined: limit === undefined
        }); // Debug log
        
        return (
          <span className={isZero ? 'char-limit-zero' : (!isUnlimited ? 'char-limit-limited' : 'char-limit-unlimited')}>
            {isZero ? 'No plan' : (!isUnlimited ? charLimit.toLocaleString() : 'Unlimited')}
          </span>
        );
      },
    },
    {
      title: 'Used Chars',
      dataIndex: 'used_chars',
      key: 'used_chars',
      className: 'used-chars-cell',
      render: (used, record) => {
        const limit = record.char_limit;
        const percentage = limit > 0 ? (used / limit * 100).toFixed(1) : 0;
        return (
          <div>
            <span style={{ fontWeight: 'bold' }}>
              {used ? used.toLocaleString() : '0'}
            </span>
            {limit > 0 && (
              <div className="used-chars-percentage">
                ({percentage}%)
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Plan Expires',
      dataIndex: 'plan_expires_at',
      key: 'plan_expires_at',
      render: (value) => {
        if (!value) {
          return (
            <Tag color="default">
              No plan
            </Tag>
          );
        }
        const expiryDate = new Date(value);
        if (Number.isNaN(expiryDate.getTime())) {
          return <Tag color="default">Invalid date</Tag>;
        }
        const now = new Date();
        const isExpired = expiryDate.getTime() <= now.getTime();
        const diffMs = expiryDate - now;
        const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
        return (
          <div>
            <span style={{ color: isExpired ? '#ff4d4f' : '#1890ff', fontWeight: 600 }}>
              {expiryDate.toLocaleDateString()}
            </span>
            <div style={{ fontSize: 12, color: isExpired ? '#ff4d4f' : '#52c41a' }}>
              {isExpired ? 'Expired' : `${daysLeft} day(s) left`}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          {!record.is_admin && (
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>User Management</h1>
        <p>Manage system users and permissions</p>
      </div>

      <Card>
        <div className="action-buttons">
          <div className="action-buttons-left">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              className="action-btn"
            >
              Add User
            </Button>
          </div>
          <div className="action-buttons-right">
            <Checkbox
              checked={isAllSelected()}
              indeterminate={isIndeterminate()}
              onChange={(e) => handleToggleSelectAll(e.target.checked)}
              style={{ marginRight: '16px' }}
            >
              Select All
            </Checkbox>
            <Popconfirm
              title={`Are you sure you want to delete ${selectedRowKeys.length} selected user(s)?`}
              description="This action cannot be undone."
              onConfirm={handleBatchDelete}
              okText="Yes"
              cancelText="No"
              disabled={selectedRowKeys.length === 0}
            >
              <Button
                icon={<DeleteOutlined />}
                className="action-btn"
                danger
                disabled={selectedRowKeys.length === 0}
              >
                Delete ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
            <div style={{ marginRight: '10px', color: '#666' }}>
              Selected: {selectedRowKeys.length}
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="search-filter-section" style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Search by username, email, or ID..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by role"
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Roles</Select.Option>
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="user">User</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                style={{ width: '100%' }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>

        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={filteredUsers || []}
            rowKey="id"
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => {
                setSelectedRowKeys(keys);
              },
            }}
            scroll={{ x: 800 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
                fetchUsers(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                fetchUsers(1, size);
              }
            }}
          />
        </div>
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please input username!' },
              { min: 3, message: 'Username must be at least 3 characters!' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please input valid email!' }
            ]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please input password!' },
                { min: 6, message: 'Password must be at least 6 characters!' }
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            name="char_limit"
            label="Character Limit"
            initialValue={10000}
            rules={[
              { 
                validator: (_, value) => {
                  if (value && (isNaN(Number(value)) || Number(value) < 0)) {
                    return Promise.reject(new Error('Character limit must be a positive number!'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              type="number" 
              placeholder="Enter character limit (leave empty for unlimited)"
              addonAfter="chars"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item
            name="plan_expires_at"
            label="Plan Expiration"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              placeholder="Select expiration date"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UserManagement;